/**
 * ClozeQuizGenerator — Quiz à trous (Carbon Design System)
 */

import { useState } from 'react';
import { Edit, ArrowLeft } from '@carbon/icons-react';
import { Button } from '@carbon/react';
import {
  FileUpload,
  LanguageSelector,
  AIProcessingCard,
  ClozeQuizDisplay,
  AiGeneratorPageLayout,
  AiGeneratorFormCard,
  AiFormStep,
  AiGenerateButton,
} from '@sphere/learning/ui';
import {
  useGenerateClozeQuiz,
  useFileValidation,
} from '@sphere/learning/data-access';
import type { SupportedLanguage, ClozeQuizSchema } from '@sphere/ai/domain';
import { handleAIError } from '@sphere/ai/data-access';
import { downloadBlob } from '@sphere/shared/util';
import tw from './ClozeQuizGenerator.tailwind.module.scss';

export default function ClozeQuizGenerator() {
  const [language, setLanguage] = useState<SupportedLanguage>(
    'fr' as SupportedLanguage
  );
  const [generatedQuiz, setGeneratedQuiz] = useState<ClozeQuizSchema | null>(
    null
  );

  const { file, validation, setFile } = useFileValidation();
  const generateClozeQuiz = useGenerateClozeQuiz();

  const handleGenerate = async () => {
    if (!file || !validation.isValid) return;
    try {
      const quiz = await generateClozeQuiz.mutateAsync({
        file,
        lang: language,
      });
      setGeneratedQuiz(quiz);
    } catch (error) {
      console.error('Cloze quiz generation failed:', error);
    }
  };

  const handleNewQuiz = () => {
    setGeneratedQuiz(null);
    setFile(null);
  };

  const handleDownloadQuiz = () => {
    if (!generatedQuiz) return;
    const content =
      `${generatedQuiz.title || 'Cloze Quiz'}\n\n` +
      generatedQuiz.questions
        .map(
          (q, i) =>
            `Question ${i + 1}:\n${q.question}\n` +
            `Answer: ${q.answer}\n` +
            `Choices: ${q.choices.join(', ')}\n---\n`
        )
        .join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    downloadBlob(blob, `cloze-quiz-${Date.now()}.txt`);
  };

  if (generatedQuiz) {
    return (
      <div className={tw.u1}>
        <div style={{ maxWidth: '90rem', margin: '0 auto' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={ArrowLeft}
              onClick={handleNewQuiz}
            >
              Retour
            </Button>
          </div>
          <ClozeQuizDisplay
            quiz={generatedQuiz}
            onDownload={handleDownloadQuiz}
            onRestart={handleNewQuiz}
          />
        </div>
      </div>
    );
  }

  return (
    <AiGeneratorPageLayout
      icon={Edit}
      title="Générateur de Quiz à Trous"
      description="Créez des exercices à trous à partir de vos documents. Un outil professionnel pour tester la compréhension et la mémorisation."
    >
      <AiGeneratorFormCard title="Créer un Quiz à Trous" icon={Edit}>
        <AiFormStep step={1} label="Sélectionnez votre document">
          <FileUpload
            file={file}
            onFileChange={setFile}
            disabled={generateClozeQuiz.isPending}
          />
          {validation.error && (
            <p className={`cds--form-requirement ${tw.u2}`}>{validation.error}</p>
          )}
        </AiFormStep>
        <AiFormStep step={2} label="Choisissez la langue">
          <LanguageSelector
            value={language}
            onChange={setLanguage}
            disabled={generateClozeQuiz.isPending}
          />
        </AiFormStep>
        <AiFormStep step={3} label="Générez votre quiz">
          <AiGenerateButton
            onClick={handleGenerate}
            disabled={
              !file || !validation.isValid || generateClozeQuiz.isPending
            }
            isLoading={generateClozeQuiz.isPending}
            label="Générer le Quiz à Trous"
            loadingLabel="Génération..."
            icon={Edit}
          />
        </AiFormStep>
      </AiGeneratorFormCard>

      {generateClozeQuiz.isPending && (
        <div className={tw.u3}>
          <AIProcessingCard
            isLoading
            error={null}
            title="Génération du quiz à trous..."
            description="L'IA analyse votre texte et identifie les mots clés à retirer..."
          />
        </div>
      )}

      {generateClozeQuiz.error && (
        <div className={tw.u3}>
          <AIProcessingCard
            isLoading={false}
            error={handleAIError(generateClozeQuiz.error)}
            title="Erreur de génération"
            onRetry={handleGenerate}
          />
        </div>
      )}
    </AiGeneratorPageLayout>
  );
}
